import { useQuery } from "@apollo/client/react";
import { Navbar, Page } from "framework7-react";
import { useSwipeable } from "react-swipeable";

import { ClockPieChart } from "@/components/ClockPieChart";
import { DailyReviewPageTitle } from "@/components/DailyReviewPageTitle";
import { DimensionLegendBar } from "@/components/DimensionLegendBar";
import { NavLeftPrev } from "@/components/NavLeftPrev";
import { NavRightEmpty } from "@/components/NavRightEmpty";
import { NavRightNext } from "@/components/NavRightNext";
import { NavTitle } from "@/components/NavTitle";
import { ReviewButtonBar } from "@/components/ReviewButtonBar";
import { formatISODateString } from "@/config/date-fns";
import { createPage, PageComponentProps } from "@/factories/createPage";
import { getFragmentData } from "@/generated/graphql";
import { EntryFragmentDoc, GetDailyDataDocument } from "@/generated/graphql/graphql";
import { usePageReady } from "@/hooks/usePageReady";
import { useCalendarStore } from "@/stores/calendarStore";

export const DailyReviewPage = createPage("DailyReviewPage", PageComponent);

function PageComponent({ pageName }: PageComponentProps) {
  const { isPageReady, getPageProps } = usePageReady(pageName, { isTabPage: true });

  const { daily } = useCalendarStore();

  const { data, refetch } = useQuery(GetDailyDataDocument, {
    skip: !isPageReady,
    variables: { date: formatISODateString(daily.date) },
  });
  const entries = getFragmentData(EntryFragmentDoc, data?.dailyEntries || []);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: daily.next,
    onSwipedRight: daily.prev,
  });

  return (
    <Page
      ptr
      ptrMousewheel
      data-component={pageName}
      onPtrRefresh={(done) => {
        refetch().then(done);
      }}
      {...getPageProps()}
    >
      <Navbar>
        <NavLeftPrev onClick={daily.prev} />
        <NavTitle>
          <DailyReviewPageTitle />
        </NavTitle>
        {daily.isToday ? <NavRightEmpty /> : <NavRightNext onClick={daily.next} />}
      </Navbar>

      <div {...swipeHandlers}>
        <ClockPieChart
          baseDate={daily.date}
          entries={entries}
        />
      </div>

      <DimensionLegendBar />

      <ReviewButtonBar activeButton="daily" />
    </Page>
  );
}
